import React, { useState } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { buildLogbuchPdfDocument, loadLogbuchPdfBrandingAssets } from './pdf/logbuchPdfBuilder';
import { buildLogbuchSingleFilename } from './pdf/logbuchPdfFilename';
import {
  calculateEffectiveServiceHoursForEntry,
  getPersistedPauseHours,
} from '../config/logbookEntryRules';
import { getComplianceLightPalette } from '../config/complianceLightStatus';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LogbookEntryEditDialog from './LogbookEntryEditDialog';
import LogbookEntryDeleteDialog from './LogbookEntryDeleteDialog';

pdfMake.addVirtualFileSystem(pdfFonts);

const SRHT_BASIC_COURSE_ENTRY_TYPE = 'Grundausbildungslehrgang SRHT';

function formatHours(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  const totalMinutes = Math.round(numeric * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} h`;
}

function TrainingHoursList({ entries, user, onUpdate, complianceLights = [] }) {
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [error, setError] = useState('');
  const [expandedKeywordEntryIds, setExpandedKeywordEntryIds] = useState({});

  const lights = Array.isArray(complianceLights) ? complianceLights : [];

  const isSrhtBasicCourseEntry = (entry) => entry?.entry_type === SRHT_BASIC_COURSE_ENTRY_TYPE;

  const handleExportPDF = async () => {
    try {
      const year = entries[0] ? new Date(entries[0].date).getFullYear() : new Date().getFullYear();
      const fullName = user?.full_name || user?.fullName || 'Unbekannt';
      const brandingAssets = await loadLogbuchPdfBrandingAssets();

      const docDefinition = buildLogbuchPdfDocument({
        fullName,
        year,
        entries,
        complianceLights: lights,
        brandingAssets,
        headerTitle: 'Logbuch',
      });

      const filename = buildLogbuchSingleFilename({ fullName, year });
      const pdf = pdfMake.createPdf(docDefinition);
      await pdf.download(filename);
    } catch (exportError) {
      console.error('PDF export failed:', exportError);
      setError('PDF-Export fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  return (
    <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Logbuch
            </Typography>
            <Tooltip title="Logbuch als PDF exportieren">
              <span>
                <IconButton color="primary" onClick={handleExportPDF} disabled={entries.length === 0}>
                  <PictureAsPdfIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Stack spacing={1}>
            {lights.map((light) => {
              const palette = getComplianceLightPalette(light);

              return (
                <Stack direction="row" spacing={1} alignItems="center" key={light.label}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: palette.bgColor,
                      border: 1,
                      borderColor: palette.borderColor,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {light.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Noch keine Einsatz- / HöRett Stunden eingetragen.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {entries.map((entry) => {
                const pauseHours = getPersistedPauseHours(entry);
                const hasPause = pauseHours > 0 || Number(entry.pause_taken) === 1 || entry.pause_taken === true;
                const hasTimeRange = Boolean(entry.time_from && entry.time_to);
                const dienstzeitHours = hasTimeRange
                  ? calculateEffectiveServiceHoursForEntry(entry)
                  : (Number.isFinite(Number(entry.service_time)) ? Number(entry.service_time) : null);
                const dienstzeit = dienstzeitHours === null ? '-' : formatHours(dienstzeitHours);

                const keywordItems = Array.isArray(entry.keywords) ? entry.keywords : [];
                const visibleKeywordItems = expandedKeywordEntryIds[entry.id] ? keywordItems : keywordItems.slice(0, 3);
                const hasHiddenKeywords = keywordItems.length > visibleKeywordItems.length;

                return (
                  <Card key={entry.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, px: { xs: 1.25, sm: 2 } }}>
                      <Stack spacing={1}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {new Date(entry.date).toLocaleDateString('de-DE')}
                            </Typography>
                            <Chip size="small" label={formatHours(entry.hours)} color="primary" />
                            <Chip size="small" label={entry.entry_type || '-'} variant="outlined" />
                            {keywordItems.length > 0 && <Chip size="small" variant="outlined" label={`Themen: ${keywordItems.length}`} />}
                            {entry.created_by_user_id && Number(entry.created_by_user_id) !== Number(user?.id) && (
                              <Tooltip title={`Veranlasst von ${entry.created_by_name || 'fremdem Benutzer'}`}>
                                <Chip size="small" color="warning" label="Fremdeintrag" />
                              </Tooltip>
                            )}
                          </Stack>

                          <Stack direction="row" spacing={1}>
                            {!isSrhtBasicCourseEntry(entry) && (
                              <Tooltip title="Eintrag bearbeiten">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => setEditingEntryId(entry.id)}
                                    disabled={Boolean(deletingEntryId)}
                                    sx={{ width: { xs: 42, sm: 34 }, height: { xs: 42, sm: 34 } }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Eintrag löschen">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeletingEntryId(entry.id)}
                                  disabled={Boolean(deletingEntryId)}
                                  sx={{ width: { xs: 42, sm: 34 }, height: { xs: 42, sm: 34 } }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 1.25 }} useFlexGap flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            {`Zeitraum: ${entry.time_from && entry.time_to ? `${entry.time_from} - ${entry.time_to}` : '-'}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`Pause: ${hasPause ? formatHours(pauseHours) : '-'}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`Vor-/Nachbereitung: ${entry.prep_time !== null && entry.prep_time !== undefined ? formatHours(entry.prep_time) : '-'}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`Dienstzeit: ${dienstzeit || '-'}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`Verantwortlich: ${entry.responsible_name || '-'}`}
                          </Typography>
                        </Stack>

                        {keywordItems.length > 0 && (
                          <Stack spacing={0.7}>
                            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                              {visibleKeywordItems.map((keyword) => (
                                <Chip
                                  key={`entry-keyword-${entry.id}-${keyword.id || keyword.raw}`}
                                  size="small"
                                  variant="outlined"
                                  label={keyword.raw || keyword}
                                />
                              ))}
                            </Stack>
                            {(hasHiddenKeywords || keywordItems.length > 3) && (
                              <Box>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setExpandedKeywordEntryIds((prev) => ({
                                    ...prev,
                                    [entry.id]: !prev[entry.id],
                                  }))}
                                >
                                  {expandedKeywordEntryIds[entry.id] ? 'Weniger anzeigen' : `Mehr anzeigen (${keywordItems.length - visibleKeywordItems.length})`}
                                </Button>
                              </Box>
                            )}
                          </Stack>
                        )}

                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          <LogbookEntryDeleteDialog
            entryId={deletingEntryId}
            open={Boolean(deletingEntryId)}
            onClose={() => setDeletingEntryId(null)}
            onDeleted={() => {
              setDeletingEntryId(null);
              onUpdate();
            }}
          />

          <LogbookEntryEditDialog
            entryId={editingEntryId}
            open={Boolean(editingEntryId)}
            onClose={() => setEditingEntryId(null)}
            onSaved={() => {
              setEditingEntryId(null);
              onUpdate();
            }}
            onUpdated={onUpdate}
          />

        </Stack>
      </CardContent>
    </Card>
  );
}

export default TrainingHoursList;
